import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { LoginModal, handleGoogleLogin } from '@/components/auth/LoginModal';
import { SectorSelect } from '@/components/ui/SectorSelect';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { 
  Key, Settings, Compass, Search,
  Star, MapPin, Briefcase, Eye, EyeOff, Building2, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const experts = [
  {
    id: 1,
    name: "Marie Laurent",
    title: "Ex-DRH",
    company: "CAC 40",
    experience: "32",
    expertise: ["Ressources Humaines", "Transformation", "Leadership"],
    location: "Paris",
    rating: 4.9,
    available: true,
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&q=80"
  },
  {
    id: 2,
    name: "Jean-Pierre Moreau",
    title: "Ex-Directeur Financier",
    company: "Groupe industriel",
    experience: "35",
    expertise: ["Finance", "M&A", "Restructuration"],
    location: "Lyon",
    rating: 4.8,
    available: true,
    image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=200&q=80"
  },
  {
    id: 3,
    name: "Catherine Dubois",
    title: "Ex-Directrice Marketing",
    company: "Luxe international",
    experience: "28",
    expertise: ["Marketing", "Stratégie de marque", "International"],
    location: "Paris",
    rating: 5.0,
    available: false,
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&q=80"
  },
  {
    id: 4,
    name: "Philippe Martin",
    title: "Ex-DSI",
    company: "Banque",
    experience: "30",
    expertise: ["IT", "Transformation digitale", "Cybersécurité"],
    location: "Bordeaux",
    rating: 4.7,
    available: true,
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&q=80"
  }
];

const EntreprisesPage = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [showContactForm, setShowContactForm] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [sectorCategories, setSectorCategories] = useState({});
  const [companySizes, setCompanySizes] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [registerData, setRegisterData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    password: '',
    contactPhone: '',
    sector: '',
    size: '',
    website: '',
    address: '',
    siret: ''
  });

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Fetch sectors and company sizes on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sectorsRes, sizesRes] = await Promise.all([
          fetch(`${API_URL}/api/sectors/by-category`),
          fetch(`${API_URL}/api/reference/company-sizes`)
        ]);
        
        if (sectorsRes.ok) {
          const data = await sectorsRes.json();
          setSectorCategories(data.sectors);
        }
        
        if (sizesRes.ok) {
          const data = await sizesRes.json();
          setCompanySizes(data.items);
        }
      } catch (error) {
        console.error('Failed to fetch reference data:', error);
      }
    };
    fetchData();
  }, []);

  const benefits = [
    {
      icon: Key,
      title: t('benefits.business.access.title'),
      description: t('benefits.business.access.description')
    },
    {
      icon: Settings,
      title: t('benefits.business.efficiency.title'),
      description: t('benefits.business.efficiency.description')
    },
    {
      icon: Compass,
      title: t('benefits.business.continuity.title'),
      description: t('benefits.business.continuity.description')
    }
  ];

  const handleContactExpert = (expert) => {
    setSelectedExpert(expert);
    setShowContactForm(true);
  };

  const handleSubmitContact = (e) => {
    e.preventDefault();
    toast.success(t('enterprisesPage.requestSent', { name: selectedExpert?.name }));
    setShowContactForm(false);
    setFormData({ company: '', name: '', email: '', phone: '', message: '' });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submitData = new FormData();
      submitData.append('company_name', registerData.companyName);
      submitData.append('contact_name', registerData.contactName);
      submitData.append('contact_email', registerData.contactEmail);
      submitData.append('password', registerData.password);
      submitData.append('contact_phone', registerData.contactPhone);
      submitData.append('sector', registerData.sector);
      submitData.append('size', registerData.size);
      submitData.append('website', registerData.website);
      submitData.append('address', registerData.address);
      submitData.append('siret', registerData.siret);
      
      const response = await fetch(`${API_URL}/api/entreprises/register`, {
        method: 'POST',
        body: submitData
      });
      
      const result = await response.json();
      
      if (response.ok) {
        // Save token and user data to auto-login
        if (result.token) {
          localStorage.setItem('sfretire_token', result.token);
          localStorage.setItem('sfretire_user', JSON.stringify(result.user));
        }
        toast.success(t('enterprisesPage.registrationSuccess'));
        setShowRegisterForm(false);
        setRegisterData({
          companyName: '', contactName: '', contactEmail: '', password: '',
          contactPhone: '', sector: '', size: '', website: '', address: '', siret: ''
        });
        // Redirect to dashboard
        window.location.href = '/dashboard/entreprise';
      } else {
        toast.error(result.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        localStorage.setItem('sfretire_token', result.token);
        localStorage.setItem('sfretire_user', JSON.stringify(result.user));
        toast.success('Connexion réussie !');
        setShowLoginForm(false);
        window.location.reload();
      } else {
        toast.error(result.detail || 'Identifiants incorrects');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredExperts = experts.filter(expert =>
    expert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expert.expertise.some(exp => exp.toLowerCase().includes(searchTerm.toLowerCase())) ||
    expert.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 section-cream">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <span className="inline-block px-4 py-1.5 bg-primary/10 text-primary text-sm font-medium rounded-full">
                  {t('enterprisesPage.badge')}
                </span>
                <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground">
                  {t('enterprisesPage.title')}
                </h1>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {t('enterprisesPage.subtitle')}
                </p>
                <div className="flex gap-4">
                  <Button variant="gold" size="lg" onClick={() => document.getElementById('experts').scrollIntoView({ behavior: 'smooth' })}>
                    {t('enterprisesPage.discoverExperts')}
                  </Button>
                  <Button variant="outline-gold" size="lg" onClick={() => setShowRegisterForm(true)}>
                    {t('enterprisesPage.registerCompany')}
                  </Button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80"
                  alt="Business meeting"
                  className="rounded-2xl shadow-elevated w-full h-[400px] object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="card-elevated p-6"
                  >
                    <div className="icon-circle mb-4">
                      <IconComponent size={24} />
                    </div>
                    <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Experts Section */}
        <section id="experts" className="py-16 lg:py-20 section-cream">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
                {t('enterprisesPage.ourExperts')}
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t('enterprisesPage.expertsSubtitle')}
              </p>
            </motion.div>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto mb-10">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                <Input
                  type="text"
                  placeholder={t('enterprisesPage.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 bg-card"
                />
              </div>
            </div>

            {/* Experts Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {filteredExperts.map((expert, index) => (
                <motion.div
                  key={expert.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-card rounded-xl p-6 border border-border/50 hover:border-primary/30 hover:shadow-medium transition-all"
                >
                  <div className="flex gap-4">
                    <img
                      src={expert.image}
                      alt={expert.name}
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-serif font-semibold text-foreground">{expert.name}</h3>
                          <p className="text-sm text-muted-foreground">{expert.title} • {expert.company}</p>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          expert.available 
                            ? 'bg-success/10 text-success' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {expert.available ? t('enterprisesPage.available') : t('enterprisesPage.onMission')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {expert.expertise.map((exp) => (
                      <span key={exp} className="px-3 py-1 bg-secondary text-secondary-foreground text-xs rounded-full">
                        {exp}
                      </span>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1">
                        <Briefcase size={14} />
                        {expert.experience} {t('enterprisesPage.yearsExp')}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {expert.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Star size={14} className="text-primary fill-primary" />
                        {expert.rating}
                      </span>
                    </div>
                    <Button 
                      variant="outline-gold" 
                      size="sm"
                      onClick={() => handleContactExpert(expert)}
                      disabled={!expert.available}
                    >
                      {t('enterprisesPage.contact')}
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Modal */}
        {showContactForm && (
          <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card rounded-2xl p-8 max-w-lg w-full shadow-elevated"
            >
              <h3 className="text-xl font-serif font-semibold text-foreground mb-2">
                {t('enterprisesPage.contactTitle')} {selectedExpert?.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {t('enterprisesPage.contactSubtitle')}
              </p>

              <form onSubmit={handleSubmitContact} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground">{t('enterprisesPage.company')}</label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    required
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.name')}</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.email')}</label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground">{t('enterprisesPage.message')}</label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder={t('enterprisesPage.describeNeed')}
                    rows={4}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowContactForm(false)} className="flex-1">
                    {t('enterprisesPage.cancel')}
                  </Button>
                  <Button type="submit" variant="gold" className="flex-1">
                    {t('enterprisesPage.sendRequest')}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Enterprise Registration Form Modal */}
        {showRegisterForm && (
          <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full shadow-elevated my-4 sm:my-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="text-primary" size={28} />
                <h3 className="text-xl sm:text-2xl font-serif font-semibold text-foreground">
                  {t('enterprisesPage.registerTitle')}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                {t('enterprisesPage.registerSubtitle')}
              </p>

              {/* Google Sign Up Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full mb-4 flex items-center justify-center gap-3 h-12"
                onClick={() => handleGoogleLogin('entreprise')}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.continueWithGoogle')}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('auth.or')}</span>
                </div>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                {/* Company Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.companyName')} *</label>
                    <Input
                      value={registerData.companyName}
                      onChange={(e) => setRegisterData({...registerData, companyName: e.target.value})}
                      required
                      className="mt-1"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.siret')}</label>
                    <Input
                      value={registerData.siret}
                      onChange={(e) => setRegisterData({...registerData, siret: e.target.value})}
                      placeholder="XXX XXX XXX XXXXX"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Sector and Size */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.sector')} *</label>
                    <div className="mt-1">
                      <SectorSelect
                        value={registerData.sector}
                        onChange={(val) => setRegisterData({...registerData, sector: val})}
                        sectors={sectorCategories}
                        placeholder={t('enterprisesPage.selectSector')}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.companySize')} *</label>
                    <select
                      value={registerData.size}
                      onChange={(e) => setRegisterData({...registerData, size: e.target.value})}
                      required
                      className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary appearance-none cursor-pointer"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
                      data-testid="size-select"
                    >
                      <option value="">{t('enterprisesPage.selectSize')}</option>
                      {companySizes.map(size => (
                        <option key={size.id} value={size.id}>{size.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.contactName')} *</label>
                    <Input
                      value={registerData.contactName}
                      onChange={(e) => setRegisterData({...registerData, contactName: e.target.value})}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.contactPhone')}</label>
                    <PhoneInput
                      value={registerData.contactPhone}
                      onChange={(val) => setRegisterData({...registerData, contactPhone: val || ''})}
                      className="mt-1"
                      data-testid="register-contact-phone"
                    />
                  </div>
                </div>

                {/* Email and Password */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.email')} *</label>
                    <Input
                      type="email"
                      value={registerData.contactEmail}
                      onChange={(e) => setRegisterData({...registerData, contactEmail: e.target.value})}
                      required
                      className="mt-1"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.password')} *</label>
                    <div className="relative mt-1">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                        minLength={6}
                        placeholder="Min. 6 caractères"
                        className="pr-10"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Website and Address */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.website')}</label>
                    <Input
                      type="url"
                      value={registerData.website}
                      onChange={(e) => setRegisterData({...registerData, website: e.target.value})}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground">{t('enterprisesPage.address')}</label>
                    <Input
                      value={registerData.address}
                      onChange={(e) => setRegisterData({...registerData, address: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowRegisterForm(false)} className="flex-1">
                    {t('enterprisesPage.cancel')}
                  </Button>
                  <Button type="submit" variant="gold" className="flex-1" disabled={isSubmitting}>
                    {isSubmitting ? t('enterprisesPage.registering') : t('enterprisesPage.register')}
                  </Button>
                </div>

                <p className="text-center text-sm text-muted-foreground pt-2">
                  {t('enterprisesPage.alreadyRegistered')}{' '}
                  <button
                    type="button"
                    onClick={() => { setShowRegisterForm(false); setShowLoginForm(true); }}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('enterprisesPage.loginLink')}
                  </button>
                </p>
              </form>
            </motion.div>
          </div>
        )}

        {/* Login Modal */}
        <LoginModal
          isOpen={showLoginForm}
          onClose={() => setShowLoginForm(false)}
          userType="entreprise"
          onSwitchToRegister={() => {
            setShowLoginForm(false);
            setShowRegisterForm(true);
          }}
        />
      </main>

      <Footer />
    </div>
  );
};

export default EntreprisesPage;
